import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, push, child } from "firebase/database";
import { Shopping } from "@interfaces/ishopping";
import { logService } from "../services/LogService";
import { authStore } from "./AuthStore";

class ShoppingStore {
  itens: Shopping[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchItens = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "shopping"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allPosts: Shopping[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.itens = allPosts.filter(item => item.active && !item.deleted);
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.itens = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar posts:", error);
      runInAction(() => {
        this.error = "Erro ao carregar posts.";
        this.loading = false;
      });
    }
  };

  saveItem = async (post: Omit<Shopping, "id">, id?: string) => {
    const itemId = id || push(ref(db, "shopping")).key;
    const dbRef = ref(db, `shopping/${itemId}`);
    
    // Verifica se é uma criação ou atualização
    const isUpdate = !!id;
    
    // Busca o valor anterior se for uma atualização
    let oldValue = null;
    if (isUpdate) {
      try {
        const snapshot = await get(dbRef);
        oldValue = snapshot.exists() ? snapshot.val() : null;
      } catch (error) {
        console.error('Erro ao buscar valor anterior:', error);
      }
    }
    
    await set(dbRef, { ...post });
    
    // Registra o log
    if (authStore._user) {
      const logData = isUpdate 
        ? logService.updateLogData(
            authStore._user.uid,
            authStore._user.email || '',
            'Shopping',
            itemId,
            `shopping/${itemId}`,
            oldValue,
            post,
            `Item de shopping ${isUpdate ? 'atualizado' : 'criado'}: ${post.title}`
          )
        : logService.createLogData(
            authStore._user.uid,
            authStore._user.email || '',
            'Shopping',
            itemId,
            `shopping/${itemId}`,
            post,
            `Item de shopping criado: ${post.title}`
          );
      
      await logService.logOperation(logData);
    }
    
    this.fetchItens();
  };

  deleteItem = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir este item?");
    if (!confirmed) return;

    // Busca o valor anterior para o log
    const dbRef = ref(db, `shopping/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `shopping/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (authStore._user && oldValue) {
      const logData = logService.deleteLogData(
        authStore._user.uid,
        authStore._user.email || '',
        'Shopping',
        id,
        `shopping/${id}`,
        oldValue,
        `Item de shopping excluído: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchItens();
  };
}

export const shoppingStore = new ShoppingStore();
